import React, { useEffect, useState } from 'react';
import { Modal, Form, Row, Col, Alert, Card } from 'react-bootstrap';
import consultationService from '../../services/consultationService';
import ClinicalButton from '../doctor/ui/ClinicalButton';

function toVital(raw, allowDecimal) {
  const s = raw === null || raw === undefined ? '' : String(raw).trim();
  if (s === '') return null;
  const n = allowDecimal ? Number(s.replace(',', '.')) : parseInt(s, 10);
  if (Number.isNaN(n)) {
    throw new Error('Constante vitale invalide — utilisez des nombres');
  }
  return n;
}

/**
 * Saisie des constantes vitales sur un passage (consultation) existant.
 * Enregistrées sur la même ligne consultation — visibles dans l’historique dossier adhérent.
 */
export default function NursingVitalsModal({ show, onHide, consultation, viewerSiteId = '', onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    weight_kg: '',
    height_cm: '',
    bp_systolic: '',
    bp_diastolic: '',
    temperature_c: '',
    heart_rate_bpm: '',
    note: ''
  });

  useEffect(() => {
    if (!consultation || !show) return;
    setError('');
    setFormData({
      weight_kg: consultation.weight_kg != null && consultation.weight_kg !== '' ? String(consultation.weight_kg) : '',
      height_cm: consultation.height_cm != null && consultation.height_cm !== '' ? String(consultation.height_cm) : '',
      bp_systolic: consultation.bp_systolic != null ? String(consultation.bp_systolic) : '',
      bp_diastolic: consultation.bp_diastolic != null ? String(consultation.bp_diastolic) : '',
      temperature_c:
        consultation.temperature_c != null && consultation.temperature_c !== ''
          ? String(consultation.temperature_c)
          : '',
      heart_rate_bpm: consultation.heart_rate_bpm != null ? String(consultation.heart_rate_bpm) : ''
      ,
      note: ''
    });
  }, [consultation, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consultation?.id) return;
    setError('');
    setLoading(true);
    try {
      const sid = parseInt(String(viewerSiteId), 10);
      const payload = {
        weight_kg: toVital(formData.weight_kg, true),
        height_cm: toVital(formData.height_cm, false),
        bp_systolic: toVital(formData.bp_systolic, false),
        bp_diastolic: toVital(formData.bp_diastolic, false),
        temperature_c: toVital(formData.temperature_c, true),
        heart_rate_bpm: toVital(formData.heart_rate_bpm, false),
        note: formData.note?.trim() || null
      };
      if (Number.isInteger(sid) && sid > 0) {
        payload.site_id = sid;
      }
      const res = await consultationService.recordNursingVitals(consultation.id, payload);
      if (!res.success) {
        throw new Error(res.message || 'Mise à jour impossible');
      }
      if (typeof onSuccess === 'function') onSuccess();
      onHide();
    } catch (err) {
      setError(err.message || 'Erreur à l’enregistrement');
    } finally {
      setLoading(false);
    }
  };

  if (!consultation) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="nr-vitals-modal__header text-white">
        <Modal.Title>
          Constantes vitales — passage #{consultation.id}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Card className="mb-3 border-secondary">
            <Card.Body className="py-2">
              <div className="small text-muted mb-1">Patient</div>
              <div>
                <strong>{consultation.patient_name}</strong>
                {consultation.member_matricule ? (
                  <span className="font-monospace ms-2">{consultation.member_matricule}</span>
                ) : null}
              </div>
              {consultation.member_company_name && (
                <div className="small text-muted">{consultation.member_company_name}</div>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-0 border-success">
            <Card.Header className="bg-success text-white py-2">
              <strong>Constantes vitales</strong>
              <div className="small opacity-90 fw-normal">
                Rubrique optionnelle — enregistrées sur ce passage, visibles dans l&apos;historique du dossier
                adhérent.
              </div>
            </Card.Header>
            <Card.Body className="pt-3">
              <Row className="g-2">
                <Col md={4} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Poids</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      min="0"
                      name="weight_kg"
                      value={formData.weight_kg}
                      onChange={handleChange}
                      placeholder="ex. 72,5"
                    />
                    <Form.Text className="text-muted">En kilogrammes</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Taille</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      min="0"
                      name="height_cm"
                      value={formData.height_cm}
                      onChange={handleChange}
                      placeholder="ex. 175"
                    />
                    <Form.Text className="text-muted">En centimètres</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Température</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      name="temperature_c"
                      value={formData.temperature_c}
                      onChange={handleChange}
                      placeholder="ex. 37,2"
                    />
                    <Form.Text className="text-muted">En degrés Celsius</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={3} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>TA systolique</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      min="0"
                      name="bp_systolic"
                      value={formData.bp_systolic}
                      onChange={handleChange}
                      placeholder="ex. 120"
                    />
                  </Form.Group>
                </Col>
                <Col md={3} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>TA diastolique</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      min="0"
                      name="bp_diastolic"
                      value={formData.bp_diastolic}
                      onChange={handleChange}
                      placeholder="ex. 80"
                    />
                    <Form.Text className="text-muted d-block">Systolique / Diastolique (mmHg)</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6} sm={12}>
                  <Form.Group className="mb-0">
                    <Form.Label>Fréquence cardiaque</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      min="0"
                      name="heart_rate_bpm"
                      value={formData.heart_rate_bpm}
                      onChange={handleChange}
                      placeholder="ex. 72"
                    />
                    <Form.Text className="text-muted">Battements par minute</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-0">
                    <Form.Label>Note clinique (optionnel)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      placeholder="Observations lors de la prise des constantes"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <ClinicalButton variant="secondary" onClick={onHide} disabled={loading}>
            Fermer
          </ClinicalButton>
          <ClinicalButton variant="save" type="submit" disabled={loading} loading={loading} icon="checkCircle">
            {loading ? 'Enregistrement…' : 'Enregistrer les constantes'}
          </ClinicalButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
